import { localeFallbackFontMap, localeFontFallbackPlaceholder } from '../constants';
import type { Motif, MotifRootVar } from '../motif';
import { mergeMotifs, MotifComponent, motifFontRampVariables, MotifScheme } from '../motif';

/**
 * Replaces the locale-specific font fallback placeholder in a font-family token value with the
 * provided replacement value. If no replacement value is provided, the placeholder is removed. Also
 * cleans up any resulting double commas in the font-family string.
 *
 * @param fontFamilyTokenValue - The original font-family CSS value containing a placeholder.
 * @param fallbackFont - The font name to substitute for the placeholder.
 * @returns
 */
export const replaceFontFallbackPlaceholder = (fontFamilyTokenValue: string, fallbackFont = '') => {
  return fontFamilyTokenValue
    .replace(localeFontFallbackPlaceholder, fallbackFont)
    .replace(/,\s*,/g, ','); // Remove double commas
};

/**
 * Type guard to check if a token name is a valid motif font ramp variable.
 *
 * @param tokenName - Token name to validate.
 */
const isFontRampToken = (tokenName: string): tokenName is MotifRootVar =>
  (motifFontRampVariables as readonly string[]).includes(tokenName);

/**
 * Builds font ramp tokens with locale-specific fallback fonts injected.
 *
 * @param baseTokens - The original font tokens to process.
 * @param fallbackFont - The locale-specific font family to inject.
 */
const buildLocalizedFontRampTokens = (
  baseTokens: Record<MotifRootVar, string>,
  fallbackFont: string
): Partial<Record<MotifRootVar, string>> => {
  const localizedTokens: Partial<Record<MotifRootVar, string>> = {};

  for (const tokenName of Object.keys(baseTokens)) {
    if (isFontRampToken(tokenName)) {
      const originalValue = baseTokens[tokenName] ?? '';
      localizedTokens[tokenName] = replaceFontFallbackPlaceholder(originalValue, fallbackFont);
    }
  }

  return localizedTokens;
};

/**
 * Returns a motif with locale-specific font-family fallbacks appended for the requested tokens.
 *
 * @param locale - The locale code used to determine fallback fonts.
 * @param baseMotif - The base motif to augment.
 * @param fontTokensToLocalize - Font-family tokens that need fallback injection.
 */
export const getMotifWithFontFallbacks = (
  locale: string,
  baseMotif?: Motif,
  fontTokensToLocalize?: Record<string, string>
) => {
  if (!baseMotif) {
    return undefined;
  }

  const fallbackFont = localeFallbackFontMap[locale];

  if (!fallbackFont || !fontTokensToLocalize) {
    return baseMotif;
  }

  /** Font ramp tokens with locale-specific fallbacks injected. */
  const localizedFontRampTokens = buildLocalizedFontRampTokens(fontTokensToLocalize, fallbackFont);

  return mergeMotifs(baseMotif, {
    name: `${baseMotif.name}Localized${locale}`,
    fontFamily: [...baseMotif.fontFamily, fallbackFont],
    [MotifScheme.DEFAULT]: {
      name: baseMotif[MotifScheme.DEFAULT].name,
      legacyName: baseMotif[MotifScheme.DEFAULT].legacyName,
      [MotifComponent.ROOT]: {
        ...baseMotif[MotifScheme.DEFAULT][MotifComponent.ROOT],
        ...localizedFontRampTokens,
      },
    },
  });
};
